import React, { useState, useEffect } from 'react';
import useAppStore from '../../store';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🎨 CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }
        
        .table-wrapper {
            width: 100%;
            overflow-x: auto;
            border-radius: 0.375rem;
            margin-bottom: 0.5rem;
            display: block;
        }
        .table-wrapper::-webkit-scrollbar { height: 8px; }
        .table-wrapper::-webkit-scrollbar-track { background: #0f172a; border-radius: 4px; }
        .table-wrapper::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
        .table-wrapper::-webkit-scrollbar-thumb:hover { background: #64748b; }

        /* V1.3 FIX: Breite auf 1400px erhöht für mehr Atemluft */
        table { width: 100%; min-width: 1400px; border-collapse: collapse; }
        th.col-tight, td.col-tight { width: 1px; white-space: nowrap; }
        
        /* V1.3 FIX: Spalten optimiert (Datum & Tage größer, Info kleiner) */
        th.col-date, td.col-date { min-width: 180px; width: 180px; } 
        th.col-day,  td.col-day  { min-width: 140px; width: 140px; } 
        th.col-info, td.col-info { min-width: 150px; width: auto; }  
        
        .input-wrapper, .table-select { 
            background-color: #000000; 
            border: 1px solid #475569; 
            border-radius: 0.25rem; 
            height: 2.8rem; 
            display: flex; 
            align-items: center; 
            width: 100%;
            transition: all 0.2s;
        }
        .input-wrapper:focus-within, .table-select:focus { 
            border-color: #E6C299; 
            box-shadow: 0 0 0 1px #E6C299; 
        }

        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }
        
        .table-input.ghost-value { color: #64748b !important; }
        .table-input.active-value { color: #ffffff !important; font-weight: 800; border-color: #94a3b8; }
        
        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }
        
        .btn-admin-lock { background-color: #1e293b; color: #64748b; border: 1px solid #334155; width: 100%; height: 2.8rem; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; border-radius: 0.25rem; font-size: 0.9rem; }
        .btn-admin-lock:hover { background-color: #334155; color: #E6C299; border-color: #E6C299; }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 📝 ROW COMPONENT: ACTIVE DRAFT
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    itemId: string;
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ActiveOpeningHoursRow: React.FC<ActiveRowProps> = ({ itemId, onAdminLock }) => {
    const { clientData, updateOpeningHoursDraft, removeOpeningHoursDraft, commitDraftState } = useAppStore();
    const draft = clientData.drafts.openingHours?.find(i => i.id === itemId);
    
    // Fallback: Wenn keine Ist-Werte da sind, nutze 0
    const originalHours = clientData.unternehmerData?.geoeffneteStunden || [0,0,0,0,0,0];

    const fmt = (val: number | undefined) => 
        val !== undefined ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val) : '';
    
    const parse = (val: string) => parseFloat(val.replace(/\./g, '').replace(',', '.')) || 0;

    const [localValues, setLocalValues] = useState<any>(() => {
        if (!draft) return {};
        return {
            ...draft.inputs,
            mon: fmt(draft.inputs.mon),
            tue: fmt(draft.inputs.tue),
            wed: fmt(draft.inputs.wed),
            thu: fmt(draft.inputs.thu),
            fri: fmt(draft.inputs.fri),
            sat: fmt(draft.inputs.sat),
        };
    });

    useEffect(() => {
        if (draft) {
            setLocalValues({
                ...draft.inputs,
                mon: fmt(draft.inputs.mon),
                tue: fmt(draft.inputs.tue),
                wed: fmt(draft.inputs.wed),
                thu: fmt(draft.inputs.thu),
                fri: fmt(draft.inputs.fri),
                sat: fmt(draft.inputs.sat),
            });
        }
    }, [draft]);

    if (!draft) return null;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLElement).blur();
        }
    };

    const handleChange = (field: string, val: string) => {
        setLocalValues((prev: any) => ({ ...prev, [field]: val }));
    };

    const handleBlur = (field: string) => {
        commitDraftState();
        let val = localValues[field];

        if (['mon', 'tue', 'wed', 'thu', 'fri', 'sat'].includes(field)) {
            const num = parse(val);
            updateOpeningHoursDraft(itemId, field, num);
            setLocalValues((prev: any) => ({ ...prev, [field]: fmt(num) }));
        } else {
            updateOpeningHoursDraft(itemId, field, val);
        }
    };

    const isLocked = draft.isLocked; // ADMIN STATUS
    const rowClass = `pe-row active-row ${isLocked ? 'locked-row' : ''}`;

    // Helper für das Ghosting (Vergleich mit Array Index)
    const getClass = (field: string, origIndex: number) => {
        const valNum = parse(localValues[field] || '0');
        const origNum = originalHours[origIndex] || 0;
        return Math.abs(valNum - origNum) > 0.01 ? 'active-value' : 'ghost-value';
    };

    // Helper für Inputs
    const renderDayInput = (field: string, label: string, origIndex: number) => (
        <td className="col-day">
            <div className="pe-tooltip-wrapper" data-tooltip={`${label} Stunden`}>
                <div className="input-wrapper">
                    <input 
                        type="text" 
                        className={`table-input ${getClass(field, origIndex)}`}
                        value={localValues[field] || ''}
                        onChange={(e) => handleChange(field, e.target.value)}
                        onBlur={() => handleBlur(field)}
                        onFocus={handleFocus}
                        onKeyDown={handleKeyDown}
                        disabled={isLocked}
                        placeholder={fmt(originalHours[origIndex] || 0)}
                    />
                    <div className="unit-label"><span className="unit-text">Std</span></div>
                </div>
            </div>
        </td>
    );

    return (
        <tr className={rowClass}>
            {/* RESET */}
            <td className="col-tight">
                <div className="pe-tooltip-wrapper" data-tooltip="Eintrag löschen">
                    <button className="reset-btn-row" onClick={() => removeOpeningHoursDraft(itemId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            {/* DATUM */}
            <td className="col-date">
                 <div className="pe-tooltip-wrapper" data-tooltip="Gültig ab">
                    <select 
                        className="table-select field-date input-active-look"
                        value={localValues.date || ''}
                        onChange={(e) => { handleChange('date', e.target.value); updateOpeningHoursDraft(itemId, 'date', e.target.value); }}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* TAGE */}
            {renderDayInput('mon', 'Montag', 0)}
            {renderDayInput('tue', 'Dienstag', 1)}
            {renderDayInput('wed', 'Mittwoch', 2)}
            {renderDayInput('thu', 'Donnerstag', 3)}
            {renderDayInput('fri', 'Freitag', 4)}
            {renderDayInput('sat', 'Samstag', 5)}

            {/* DETAIL / NOTIZ */}
            <td className="col-info">
                <div className="input-wrapper">
                     <input 
                        type="text" 
                        className="table-input text-left pl-3 text-sm text-pe-muted dark:text-slate-400" 
                        placeholder="Notiz / Details..."
                        value={localValues.notes || ''} 
                        onChange={(e) => handleChange('notes', e.target.value)}
                        onBlur={() => handleBlur('notes')}
                        onKeyDown={handleKeyDown}
                        disabled={isLocked}
                    />
                </div>
            </td>

             {/* ADMIN SPERRE */}
             <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(itemId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(itemId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🃏 JOKER ROW (ADD NEW)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerOpeningHoursRow: React.FC = () => {
    const { addOpeningHoursDraft, commitDraftState, updateOpeningHoursDraft, clientData } = useAppStore();
    const [selectedDate, setSelectedDate] = useState('');
    const originalHours = clientData.unternehmerData?.geoeffneteStunden || [0,0,0,0,0,0];

    const fmt = (val: number | undefined) => 
        val !== undefined ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val) : '';

    const handleDateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        
        if (date) {
            commitDraftState();
            addOpeningHoursDraft();
            
            setTimeout(() => {
                const drafts = useAppStore.getState().clientData.drafts.openingHours;
                if (drafts && drafts.length > 0) {
                    const lastItem = drafts[drafts.length - 1];
                    updateOpeningHoursDraft(lastItem.id, 'date', date);
                }
            }, 0);
            
            setSelectedDate('');
        }
    };

    const renderGhostDay = (origIndex: number) => (
        <td className="col-day">
            <div className="input-wrapper opacity-50">
                <input type="text" className="table-input ghost-value" disabled placeholder={fmt(originalHours[origIndex] || 0)} />
                <div className="unit-label"><span className="unit-text">Std</span></div>
            </div>
        </td>
    );

    return (
        <tr className="pe-row">
            <td className="col-tight"><button className="reset-btn-row" disabled><i className="fas fa-plus text-stone-300 dark:text-slate-600"></i></button></td>
            
            {/* TRIGGER: DATE SELECT */}
            <td className="col-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Datum wählen zum Ändern">
                    <select 
                        className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} 
                        value={selectedDate} 
                        onChange={handleDateSelect}
                    >
                        <option value="">Datum wählen</option>
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* GHOST CELLS (Pre-Filled values from DB) */}
            {renderGhostDay(0)}
            {renderGhostDay(1)}
            {renderGhostDay(2)}
            {renderGhostDay(3)}
            {renderGhostDay(4)}
            {renderGhostDay(5)}

            <td className="col-info">
                <div className="table-input ghost-value opacity-20 text-left pl-3" style={{ lineHeight: '2.8rem' }}>---</div>
            </td>
            <td className="col-tight"><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 🚀 MAIN COMPONENT
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface OpeningHoursTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const OpeningHoursTable: React.FC<OpeningHoursTableProps> = ({ onAdminLock }) => {
    const { clientData } = useAppStore();
    const drafts = clientData.drafts.openingHours || [];

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2">Reset</th>
                        <th className="col-date px-2">Gültig ab</th>
                        <th className="col-day px-2">Montag</th>
                        <th className="col-day px-2">Dienstag</th>
                        <th className="col-day px-2">Mittwoch</th>
                        <th className="col-day px-2">Donnerstag</th>
                        <th className="col-day px-2">Freitag</th>
                        <th className="col-day px-2">Samstag</th>
                        <th className="col-info px-2">Info / Notizen</th>
                        <th className="col-tight px-2">Admin</th>
                    </tr>
                </thead>
                <tbody id="openingHoursBody">
                    {drafts.map(item => (
                        <ActiveOpeningHoursRow key={item.id} itemId={item.id} onAdminLock={onAdminLock} />
                    ))}
                    <JokerOpeningHoursRow />
                </tbody>
            </table>
        </div>
    );
};

export default OpeningHoursTable;